import { useEffect, useState, useCallback } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import PdfUpload from "@/components/PdfUpload";
import MultiImageUpload from "@/components/MultiImageUpload";
import { Plus, X, Edit3, Trash2, Check, X as XIcon, ExternalLink, Download, Clock, CheckCircle2, XCircle, FileText, Folder, Search } from "lucide-react";

const TYPES = ["document", "video", "template", "study", "toolkit"];
const CATEGORIES = ["Marketing", "Légal", "Tech", "Vente", "RH", "Finance", "Production", "Innovation", "Général"];
const STATUSES = [
  { id: "all", label: "Toutes" },
  { id: "pending", label: "En attente", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  { id: "published", label: "Publiées", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { id: "rejected", label: "Rejetées", cls: "border-rose-300 bg-rose-50 text-rose-700" },
];

function emptyForm() {
  return { title: "", description: "", category: "Général", type: "document", pdf_url: "", external_url: "", cover_image: "", access: "member", images: [] };
}

function StatusBadge({ status }) {
  const m = { pending: { l: "En attente", I: Clock, c: "bg-amber-50 text-amber-700" }, published: { l: "Publiée", I: CheckCircle2, c: "bg-emerald-50 text-emerald-700" }, rejected: { l: "Rejetée", I: XCircle, c: "bg-rose-50 text-rose-700" } }[status] || { l: status, I: Clock, c: "bg-neutral-100 text-neutral-600" };
  const I = m.I;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${m.c}`}><I className="h-2.5 w-2.5" />{m.l}</span>;
}

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(() => {
    const url = filter === "all" ? "/admin/resources" : `/admin/resources?status=${filter}`;
    api.get(url).then((r) => setItems(r.data || [])).catch(() => toast.error("Erreur de chargement"));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setForm({ ...emptyForm(), ...r }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) {
        await api.patch(`/admin/resources/${edit.id}`, form);
        toast.success("Ressource mise à jour");
      } else {
        await api.post("/admin/resources", form);
        toast.success("Ressource créée et publiée");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const approve = async (r) => {
    try { await api.post(`/admin/resources/${r.id}/approve`); toast.success(`✅ "${r.title}" publiée — +8 pts pour ${r.submitted_by_name || "le membre"}`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const reject = async (r) => {
    const reason = window.prompt(`Raison du rejet pour "${r.title}" ?`, "");
    if (reason === null) return;
    try { await api.post(`/admin/resources/${r.id}/reject`, { reason }); toast.success("Ressource rejetée — email envoyé au membre"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Supprimer "${r.title}" ?`)) return;
    try { await api.delete(`/admin/resources/${r.id}`); toast.success("Supprimée"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = items.filter((r) => !search || r.title?.toLowerCase().includes(search.toLowerCase()) || r.description?.toLowerCase().includes(search.toLowerCase()));
  const pendingCount = items.filter((r) => r.status === "pending").length;

  return (
    <PortalLayout sidebar="admin">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl"><Folder className="mr-2 inline h-7 w-7 text-wine-500" />Ressources</h1>
          <p className="mt-1 text-neutral-600">Bibliothèque de ressources pour les membres. {pendingCount > 0 && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-800" data-testid="pending-count">⏳ {pendingCount} en attente de validation</span>}</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="resource-new-btn">
          <Plus className="h-4 w-4" />Nouvelle ressource
        </button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex gap-1 rounded-full bg-neutral-100 p-1">
          {STATUSES.map((s) => {
            const active = filter === s.id;
            const count = s.id === "all" ? items.length : items.filter((r) => r.status === s.id).length;
            return (
              <button key={s.id} onClick={() => setFilter(s.id)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${active ? "bg-white text-wine-700 shadow-sm" : "text-neutral-600"}`} data-testid={`resource-filter-${s.id}`}>
                {s.label} ({count})
              </button>
            );
          })}
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-neutral-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…" className="rounded-full border border-neutral-200 bg-white pl-8 pr-3 py-2 text-sm" />
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[800px] text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-3 text-left">Titre</th>
              <th className="px-4 py-3 text-left">Catégorie / Type</th>
              <th className="px-4 py-3 text-left">Soumis par</th>
              <th className="px-4 py-3 text-left">Statut</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">Aucune ressource</td></tr>}
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100" data-testid={`admin-resource-row-${r.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-neutral-900">{r.title}</div>
                  <div className="line-clamp-1 max-w-md text-xs text-neutral-500">{r.description}</div>
                  <div className="mt-1 flex gap-2 text-[10px]">
                    {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noreferrer" className="text-wine-600 hover:underline"><Download className="inline h-3 w-3" /> PDF</a>}
                    {r.external_url && <a href={r.external_url} target="_blank" rel="noreferrer" className="text-wine-600 hover:underline"><ExternalLink className="inline h-3 w-3" /> Lien</a>}
                    {r.images?.length > 0 && <span className="text-neutral-500" data-testid={`admin-resource-imgcount-${r.id}`}>🖼️ {r.images.length} image{r.images.length > 1 ? "s" : ""}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-neutral-600">{r.category} · {r.type}</td>
                <td className="px-4 py-3 text-xs text-neutral-600">{r.submitted_by_name || r.submitted_by_email || "Admin"}</td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    {r.status === "pending" && (
                      <>
                        <button onClick={() => approve(r)} className="grid h-7 w-7 place-items-center rounded-full text-emerald-600 hover:bg-emerald-50" title="Approuver" data-testid={`resource-approve-${r.id}`}><Check className="h-3.5 w-3.5" /></button>
                        <button onClick={() => reject(r)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Rejeter" data-testid={`resource-reject-${r.id}`}><XIcon className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                    <button onClick={() => openEdit(r)} className="grid h-7 w-7 place-items-center rounded-full text-blue-600 hover:bg-blue-50" title="Modifier" data-testid={`resource-edit-${r.id}`}><Edit3 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(r)} className="grid h-7 w-7 place-items-center rounded-full text-rose-600 hover:bg-rose-50" title="Supprimer" data-testid={`resource-del-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-resource-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-navy">{edit ? <><Edit3 className="mr-1 inline h-5 w-5" /> Modifier la ressource</> : <><Plus className="mr-1 inline h-5 w-5" /> Nouvelle ressource</>}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-neutral-400 hover:text-neutral-700"><X className="h-5 w-5" /></button>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-title" />
              <textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-description" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-type">
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={form.access} onChange={(e) => setForm({ ...form, access: e.target.value })} className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-access">
                <option value="public">🌐 Accès public</option>
                <option value="member">👥 Réservée aux membres</option>
                <option value="press">📰 Réservée presse</option>
              </select>
              <div className="md:col-span-2">
                <PdfUpload value={form.pdf_url} onChange={({ url }) => setForm({ ...form, pdf_url: url })} label="Fichier PDF (optionnel)" testId="admin-resource-pdf" />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Images téléchargeables (PNG / JPG)</label>
                <MultiImageUpload value={form.images || []} onChange={(images) => setForm((f) => ({ ...f, images }))} testId="admin-resource-images" />
              </div>
              <input value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="OU lien externe (URL)" className="md:col-span-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="admin-resource-url" />
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Image de couverture</label>
                <ImageUpload value={form.cover_image} onChange={(url) => setForm({ ...form, cover_image: url })} label="" testId="admin-resource-cover" />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-neutral-300 px-4 py-2 text-sm">Annuler</button>
              <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="admin-resource-save">
                <FileText className="mr-1 inline h-4 w-4" />{edit ? "Mettre à jour" : "Publier"}
              </button>
            </div>
          </form>
        </div>
      )}
    </PortalLayout>
  );
}
